import { useState, useEffect } from 'react';
import { IconButton, Box, Typography, Select, MenuItem, Button, SelectChangeEvent, useTheme } from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import CloseIcon from '@mui/icons-material/Close';
import { useAppSelector } from '../../hooks/reduxHooks';
import { RootState } from '../../store';
import ChatGPTWindow from '../ChatGPTWindow';
import { Handover } from '@handover/shared-types';
import { find } from 'lodash';
import { useGetHandoverTaskQuery } from '../../redux/task/handoverTaskApi';
import { useAppDispatch } from '../../hooks/reduxHooks';
import { setHandoverTaskDocState } from '../../redux/task/handoverTaskSlice';

interface ChatGPTHESummarizerProps {
  onClose: () => void;
}

const ChatGPTHESummarizer = ({ onClose }: ChatGPTHESummarizerProps) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const [selectedHandover, setSelectedHandover] = useState<Handover>();
  const handovers = useAppSelector((state: RootState) => state.handover.handoverDocData as Handover[]);
  const { data: handoverTaskResponse } = useGetHandoverTaskQuery(selectedHandover?.id as string);
  useEffect(() => {
    if (handoverTaskResponse) {
      dispatch(setHandoverTaskDocState(handoverTaskResponse.handoverTaskDoc.tasks));
    }
  }, [handoverTaskResponse, dispatch]);
  const handleHandoverSelect = (event: SelectChangeEvent<string>) => {
    const handoverId = event.target.value;
    const selected = find(handovers, handover => handover.id === handoverId);
    setSelectedHandover(selected);
  };

  useEffect(() => {
    if (handovers.length > 0 && !selectedHandover) {
      setSelectedHandover(handovers[0]);
    }
  }, [handovers]);

  return (
    <ChatGPTWindow
      default={{
        x: window.innerWidth / 2 - 250,
        y: window.innerHeight / 2 - 250,
        width: 700,
        height: 600,
      }}
    >
      <Box sx={styles.dialog}>
        <Box
          className="draggable-handle"
          sx={{
            ...styles.dialogTitle,
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
          }}
        >
          <Box sx={styles.titleText}>Summarize Handover</Box>
          <IconButton aria-label="close" onClick={onClose} sx={styles.closeButton}>
            <CloseIcon />
          </IconButton>
        </Box>

        {handovers && handovers.length > 0 ? (
          <>
            <Grid2
              container
              spacing={2}
              sx={{
                height: 'calc(100% - 150px)',
                padding: 2,
                display: 'flex',
              }}
            >
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <Typography>Handover Doc</Typography>
                <Select
                  value={selectedHandover?.id || ''}
                  onChange={handleHandoverSelect}
                  displayEmpty
                  fullWidth
                  sx={styles.select}
                >
                  <MenuItem value="">
                    <em>SELECT HANDOVER DOC</em>
                  </MenuItem>
                  {handovers.map((handover: Handover) => (
                    <MenuItem key={handover.id} value={handover.id}>
                      <strong>{handover.id}</strong>
                    </MenuItem>
                  ))}
                </Select>
              </Grid2>

              <Grid2 size={{ xs: 12, sm: 6 }}>
                {selectedHandover ? (
                  <>
                    <Typography variant="body2">
                      <strong>ID:</strong> {selectedHandover.id}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Task:</strong> {selectedHandover.task}
                    </Typography>
                    <Typography variant="body2">
                      <strong>info:</strong> {selectedHandover.info}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Created By:</strong> {selectedHandover.created?.createdBy}
                    </Typography>
                    {selectedHandover.updated?.map(updated => (
                      <Typography variant="body2">
                        <strong>Updated By:</strong> {updated.updatedBy}
                      </Typography>
                    ))}
                  </>
                ) : (
                  <Typography variant="body2">Please select a handover from the list.</Typography>
                )}
              </Grid2>
            </Grid2>

            <Button
              variant="contained"
              color="primary"
              sx={{
                ...styles.addButton,
                backgroundColor: theme.palette.primary.main,
                '&:hover': { backgroundColor: theme.palette.primary.dark },
              }}
            >
              <strong>AI SUMMARIZE HANDOVER</strong>
            </Button>
          </>
        ) : (
          <Typography>No handovers available.</Typography>
        )}
      </Box>
    </ChatGPTWindow>
  );
};

const styles = {
  dialog: {
    height: '100%',
    width: '100%',
    display: 'flex',
    flexDirection: 'column' as const,
    position: 'relative',
    '@media (max-width: 600px)': {
      flexDirection: 'column', // Ensure proper stacking for small screens
    },
  },
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: '1px solid #ccc',
    padding: '8px',
    cursor: 'move',
  },
  titleText: {
    flex: 1,
    textAlign: 'left',
    fontSize: '18px',
    fontWeight: 'bold',
  },
  closeButton: {
    color: 'inherit',
  },
  select: {
    marginBottom: '16px',
    fontWeight: 'bold',
    color: '#000',
  },
  addButton: {
    marginTop: '16px',
    width: '100%',
  },
};

export default ChatGPTHESummarizer;
